"use client"

import * as React from "react"
import { useNavigate } from "react-router"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import { AllTermsTable } from "./all-terms-table"
import { EVAL_DASHBOARD_SECTION_H2_CLASS } from "@/lib/evaluation-dashboard-a11y"
import { evaluationTermsPath } from "@/lib/evaluation-routes"
import { useProduct } from "@/contexts/product-context"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"

interface OtherTermsSectionProps {
  pastTerms: EvaluationTerm[]
  upcomingTerms: EvaluationTerm[]
  futureTerms: EvaluationTerm[]
}

function sortTermsChronologically(terms: EvaluationTerm[]): EvaluationTerm[] {
  return [...terms].sort((a, b) => a.startDate.localeCompare(b.startDate))
}

export function OtherTermsSection({
  pastTerms,
  upcomingTerms,
  futureTerms,
}: OtherTermsSectionProps) {
  const { product } = useProduct()
  const navigate = useNavigate()
  const termsPath = evaluationTermsPath(product)

  const pastRows = React.useMemo(() => sortTermsChronologically(pastTerms), [pastTerms])
  const futureRows = React.useMemo(
    () => sortTermsChronologically([...upcomingTerms, ...futureTerms]),
    [upcomingTerms, futureTerms],
  )

  if (pastRows.length === 0 && futureRows.length === 0) {
    return null
  }

  const defaultTab = pastRows.length > 0 ? "past" : "future"
  const totalCount = pastRows.length + futureRows.length

  return (
    <section
      id="eval-other-terms"
      aria-labelledby="eval-other-terms-heading"
      className="flex scroll-mt-6 flex-col gap-4"
    >
      <DashboardSectionTitle
        as="h2"
        id="eval-other-terms-heading"
        className={EVAL_DASHBOARD_SECTION_H2_CLASS}
      >
        Other terms ({totalCount})
      </DashboardSectionTitle>

      <Card size="sm" className="flex flex-col">
        <CardContent className="flex flex-col gap-4">
          <Tabs defaultValue={defaultTab} className="flex flex-col gap-4">
            <TabsList aria-label="Other terms by timeline">
              {pastRows.length > 0 ? (
                <TabsTrigger value="past">Past ({pastRows.length})</TabsTrigger>
              ) : null}
              {futureRows.length > 0 ? (
                <TabsTrigger value="future">Future ({futureRows.length})</TabsTrigger>
              ) : null}
            </TabsList>

            {pastRows.length > 0 ? (
              <TabsContent value="past" id="eval-other-terms-past" className="mt-0">
                <AllTermsTable
                  terms={pastRows}
                  embeddedPreview
                  onEmbeddedPreviewViewMore={() => navigate(termsPath)}
                />
              </TabsContent>
            ) : null}

            {futureRows.length > 0 ? (
              <TabsContent value="future" id="eval-other-terms-future" className="mt-0">
                <AllTermsTable
                  terms={futureRows}
                  embeddedPreview
                  onEmbeddedPreviewViewMore={() => navigate(termsPath)}
                />
              </TabsContent>
            ) : null}
          </Tabs>
        </CardContent>
      </Card>
    </section>
  )
}

/** @deprecated Use OtherTermsSection */
export const AllTermsSection = OtherTermsSection
